import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import Notification from "../../../components/Notification";
import { apiCall } from "../../../utils/apiCall";
import { REMINDERS } from "./issueBits";

const OTHER = "other";
// The title is a summary: it goes into SMS messages and the list, so it is kept short.
const TITLE_MAX = 50;

// Log a new issue, or edit one: title, type (or "Other", typed in), the people
// responsible (members of the portal, or names typed in), a deadline and details.
function IssueForm() {
    const { id } = useParams();
    const editing = Boolean(id);
    const navigate = useNavigate();

    const [types, setTypes] = useState([]);
    const [members, setMembers] = useState([]);
    const [form, setForm] = useState({ title: "", typeChoice: "", other_type: "", description: "", deadline: "", reminder_frequency: "weekly" });
    const [people, setPeople] = useState([]); // react-select values
    const [errors, setErrors] = useState({});
    const [notice, setNotice] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        document.title = `${editing ? "Edit issue" : "Log an issue"} - CFCM Portal`;

        apiCall("/issues/types?active=1").then(({ ok, data }) => ok && setTypes(data)).catch(() => {});
        // Members who can be made responsible: active, expired or awaiting payment.
        apiCall("/users?paginate=false&status=all").then(({ ok, data }) => ok && Array.isArray(data) && setMembers(data)).catch(() => {});

        if (editing) {
            apiCall(`/issues/${id}`).then(({ ok, data }) => {
                if (!ok) return setNotice({ type: "danger", message: data?.message || "The issue could not be loaded." });
                setForm({
                    title: data.title,
                    typeChoice: data.issue_type_id ? String(data.issue_type_id) : OTHER,
                    other_type: data.other_type || "",
                    description: data.description || "",
                    deadline: data.deadline || "",
                    reminder_frequency: data.reminder_frequency || "weekly",
                });
                setPeople(data.assignees.map((a) => (a.user_id
                    ? { value: `u${a.user_id}`, label: a.name, userId: a.user_id }
                    : { value: `n${a.id}`, label: a.name, name: a.name })));
                // The issue's type may have been deactivated since: keep it in the list.
                if (data.issue_type_id) {
                    setTypes((current) => current.some((t) => t.id === data.issue_type_id) ? current : [...current, { id: data.issue_type_id, name: data.type_name }]);
                }
            });
        }
    }, [editing, id]);

    const memberOptions = members.map((m) => ({
        value: `u${m.id}`,
        label: `${m.first_name} ${m.last_name}`,
        email: m.email,
        userId: m.id,
    }));

    const change = (event) => setForm({ ...form, [event.target.name]: event.target.value });

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setErrors({});

        const body = {
            title: form.title,
            issue_type_id: form.typeChoice && form.typeChoice !== OTHER ? Number(form.typeChoice) : null,
            other_type: form.typeChoice === OTHER ? form.other_type : null,
            description: form.description || null,
            deadline: form.deadline || null,
            reminder_frequency: form.reminder_frequency,
            assignees: people.map((p) => (p.userId ? { user_id: p.userId } : { name: p.name || p.label })),
        };

        try {
            const { ok, data } = await apiCall(editing ? `/issues/${id}` : "/issues", { method: editing ? "PUT" : "POST", body });
            if (ok) return navigate(`/dashboard/issues/${data.id}`, { state: { saved: true } });

            const fieldErrors = Object.fromEntries(Object.entries(data || {}).filter(([, v]) => Array.isArray(v)));
            setErrors(fieldErrors);
            setNotice({ type: "danger", message: data?.message || "The issue could not be saved." });
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (problem) {
            setNotice({ type: "danger", message: "The issue could not be saved. Please try again." });
        } finally {
            setSaving(false);
        }
    };

    const fieldError = (name) => errors[name] && <p className="help is-danger">{errors[name].join(", ")}</p>;

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item"><p className="title is-4">{editing ? "Edit issue" : "Log an issue"}</p></div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to={editing ? `/dashboard/issues/${id}` : "/dashboard/issues"} className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back
                        </Link>
                    </p>
                </div>
            </nav>

            {notice && <Notification type={notice.type} message={notice.message} onClose={() => setNotice(null)} />}

            <form className="box" onSubmit={submit}>
                <div className="columns">
                    <div className="column">
                        <div className="field">
                            <label className="label" htmlFor="issue-type">Type</label>
                            <div className={`select is-fullwidth ${errors.issue_type_id ? "is-danger" : ""}`}>
                                <select id="issue-type" name="typeChoice" value={form.typeChoice} onChange={change} required>
                                    <option value="">Choose a type</option>
                                    {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    <option value={OTHER}>Other (type it in)</option>
                                </select>
                            </div>
                            {fieldError("issue_type_id")}
                        </div>
                        {form.typeChoice === OTHER && (
                            <div className="field">
                                <label className="label" htmlFor="issue-other">Type it in</label>
                                <input id="issue-other" name="other_type" className="input" value={form.other_type} onChange={change} placeholder="e.g. Insurance renewal" maxLength={100} required />
                                <p className="help">To offer it in the list next time, add it under <Link to="/dashboard/issue-types">Issue types</Link>.</p>
                            </div>
                        )}
                    </div>
                    <div className="column">
                        <div className="field">
                            <label className="label" htmlFor="issue-deadline">Due Date</label>
                            <input id="issue-deadline" type="date" name="deadline" className={`input ${errors.deadline ? "is-danger" : ""}`} value={form.deadline} onChange={change} />
                            <p className="help">The people involved are reminded 3 days before it.</p>
                            {fieldError("deadline")}
                        </div>
                        <div className="field">
                            <label className="label" htmlFor="issue-reminders">Regular reminders</label>
                            <div className="select is-fullwidth">
                                <select id="issue-reminders" name="reminder_frequency" value={form.reminder_frequency} onChange={change}>
                                    {REMINDERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </select>
                            </div>
                            <p className="help">{REMINDERS.find((r) => r.value === form.reminder_frequency)?.help} To stop reminders altogether, use "Stop notifications" on the issue's page.</p>
                        </div>
                    </div>
                </div>

                <div className="field">
                    <label className="label" htmlFor="issue-title">What needs to be done? (a short summary)</label>
                    <input id="issue-title" name="title" className={`input ${errors.title ? "is-danger" : ""}`} value={form.title} onChange={change} placeholder="e.g. Renew the cfcm.co.ke hosting" maxLength={TITLE_MAX} required aria-describedby="issue-title-help" />
                    <p className="help" id="issue-title-help">
                        A few words, like a headline ({form.title.length}/{TITLE_MAX} characters). Put the rest under "Details" below.
                    </p>
                    {fieldError("title")}
                </div>

                <div className="field">
                    <label className="label" htmlFor="issue-people">Responsible</label>
                    <CreatableSelect
                        inputId="issue-people"
                        isMulti
                        options={memberOptions}
                        value={people}
                        onChange={(value) => setPeople(value || [])}
                        onCreateOption={(text) => {
                            // A name typed in that is a member's name picks that member, so nobody is listed twice.
                            const name = text.trim().replace(/\s+/g, " ");
                            const member = memberOptions.find((m) => m.label.toLowerCase() === name.toLowerCase());
                            const chosen = member || { value: `n${Date.now()}`, label: name, name };
                            if (!people.some((p) => p.value === chosen.value || p.label.toLowerCase() === name.toLowerCase())) {
                                setPeople([...people, chosen]);
                            }
                        }}
                        formatCreateLabel={(text) => `Add "${text}" (not a member of the portal)`}
                        formatOptionLabel={(option, { context }) => (context === "menu" && option.email ? `${option.label} (${option.email})` : option.label)}
                        placeholder="Choose members, or type a name and press Enter"
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                    <p className="help">Members chosen here get an email and an SMS. Names typed in are only recorded.</p>
                </div>

                <div className="field">
                    <label className="label" htmlFor="issue-description">Details (optional)</label>
                    <textarea id="issue-description" name="description" className="textarea" rows="4" value={form.description} onChange={change} placeholder="Anything the people responsible need to know: amounts, contacts, account numbers to use..." />
                </div>

                <div className="field">
                    <button type="submit" className={`button primary-bg ${saving ? "is-loading" : ""}`} disabled={saving}>
                        {editing ? "Save changes" : "Log the issue"}
                    </button>
                </div>
            </form>
        </main>
    );
}

export default IssueForm;
